import { computed, signal } from "@preact/signals-core";
import { ApiRequestError } from "../lib/api";
import { bindErrorText, bindShow, bindValue, html, list } from "../lib/bind";
import { bindCheckbox, bindSelect, setSelectOptions } from "../lib/formBind";
import { form } from "../lib/form";
import { formatDate } from "../lib/date";
import { formatMoney, parseMoneyInput, subtractMoney, sumMoney } from "../lib/money";
import { currentScope } from "../lib/scope";
import { labelRow } from "../lib/table";
import { toast } from "../lib/toast";
import { buildBankingSubNav } from "./banking-subnav";
import { type BankAccount, listBankAccounts } from "../services/bank";
import { type Category, createCategoryRule, listCategories } from "../services/category";
import { type InvoiceSummary, listInvoices } from "../services/invoice";
import {
  type LoanSummary,
  type SplitSuggestion,
  getSplitSuggestion,
  listLoans,
  loanOptionLabel,
} from "../services/loan";
import { setTransactionAllocations, unlinkTransactionInvoice } from "../services/reconciliation";
import {
  type BankTransaction,
  type TransactionSplitInput,
  bulkUpdateTransactions,
  listTransactions,
  replaceTransactionSplits,
  updateTransaction,
} from "../services/transaction";

const TRANSACTIONS_PAGE_SIZE = 50;
/** The link-to-invoice dialog has no pager, so this cap is a silent truncation unless
 * the count below the results says so — a company loading a year of history easily has
 * more unpaid invoices than this. Named so the message can't drift from the query. */
const LINK_INVOICE_PAGE_SIZE = 20;

/** One invoice and the part of the receipt applied to it, while the dialog is open.
 * Carries the number and client so the allocated list reads without a second lookup. */
interface AllocationRow {
  invoice_id: string;
  invoice_number: string | null;
  client_name: string;
  amount: string;
  /** What the invoice would still owe if this receipt weren't allocated to it at all.
   * The row's outcome line is measured against this, so reopening a saved allocation
   * doesn't subtract it twice. */
  base_outstanding: string;
}

/** What this allocation does to the invoice, as opposed to what it does to the receipt.
 * The summary line above answers "how much of the money is spoken for"; only this
 * answers "is the invoice actually settled" — the two coincide often enough that
 * without it an equal-but-short allocation reads as paid in full. */
function absMoney(value: string): string {
  return value.startsWith("-") ? value.slice(1) : value;
}

function describeAllocationOutcome(row: AllocationRow): string {
  const amount = parseMoneyInput(row.amount);
  if (amount === null) return "";
  const remaining = subtractMoney(row.base_outstanding, amount);
  if (remaining === "0.00") return "settles it in full";
  if (!remaining.startsWith("-")) return `leaves ${formatMoney(remaining)} outstanding`;
  return `overpays by ${formatMoney(subtractMoney(amount, row.base_outstanding))}`;
}

const NO_MATCH_EXPLANATIONS: Record<string, string> = {
  no_schedule: "no repayment schedule is loaded for this loan",
  no_candidate_in_window: "no instalment falls near this date",
  amount_outside_tolerance: "no instalment is close enough to this amount",
  ambiguous: "two instalments fit equally well",
  interest_exceeds_payment: "the instalment's interest exceeds this payment",
};

/** The lender's own figures and the app's amortisation estimate must never look alike —
 * one is evidence, the other is a guess. */
function describeSuggestion(suggestion: SplitSuggestion): string {
  if (suggestion.source === "schedule") {
    const due = suggestion.due_date ? `, due ${formatDate(suggestion.due_date)}` : "";
    const base = `Lender's figures — instalment ${String(suggestion.instalment_number)}${due}.`;
    return suggestion.adjusted
      ? `${base} The payment differs from the scheduled total, so the interest is the lender's and the principal absorbs the difference.`
      : base;
  }
  const why = suggestion.fallback_reason
    ? (NO_MATCH_EXPLANATIONS[suggestion.fallback_reason] ?? suggestion.fallback_reason)
    : "";
  return why
    ? `Estimated from the loan's rate — ${why}.`
    : "Estimated from the loan's rate.";
}

export function bankingView(): Node {
  const accounts = signal<BankAccount[]>([]);
  const categories = signal<Category[]>([]);
  const loans = signal<LoanSummary[]>([]);
  const loadError = signal("");

  const transactions = signal<BankTransaction[]>([]);
  const total = signal(0);
  const page = signal(1);

  function reloadAccounts(): void {
    listBankAccounts()
      .then((result) => {
        accounts.value = result;
      })
      .catch((err: unknown) => {
        loadError.value = err instanceof ApiRequestError ? err.body.message : "Failed to load";
      });
  }
  function reloadCategories(): void {
    listCategories()
      .then((result) => {
        categories.value = result;
      })
      .catch(() => undefined);
  }
  function reloadLoans(): void {
    listLoans()
      .then((result) => {
        loans.value = result;
      })
      .catch(() => undefined);
  }

  const accountFilter = signal("");
  const dateFromFilter = signal("");
  const dateToFilter = signal("");
  const uncategorisedOnly = signal(false);
  const searchQuery = signal("");

  function reloadTransactions(): void {
    listTransactions({
      account: accountFilter.value || undefined,
      date_from: dateFromFilter.value || undefined,
      date_to: dateToFilter.value || undefined,
      uncategorised: uncategorisedOnly.value,
      q: searchQuery.value || undefined,
      page: page.value,
      page_size: TRANSACTIONS_PAGE_SIZE,
    })
      .then((result) => {
        transactions.value = result.items;
        total.value = result.total;
        selectedIds.value = new Set();
      })
      .catch((err: unknown) => {
        loadError.value = err instanceof ApiRequestError ? err.body.message : "Failed to load";
      });
  }
  function filtersChanged(): void {
    page.value = 1;
    reloadTransactions();
  }

  reloadAccounts();
  reloadCategories();
  reloadLoans();
  reloadTransactions();

  // --- Bulk actions ---
  const selectedIds = signal<Set<string>>(new Set());
  const bulkCategoryId = signal("");
  const bulkIsTransfer = signal<"" | "true" | "false">("");
  const bulkIsPersonal = signal<"" | "true" | "false">("");
  function toggleSelected(id: string, checked: boolean): void {
    const next = new Set(selectedIds.value);
    if (checked) next.add(id);
    else next.delete(id);
    selectedIds.value = next;
  }
  async function applyBulk(): Promise<void> {
    if (selectedIds.value.size === 0) {
      toast.error("Select at least one transaction.");
      return;
    }
    if (!bulkCategoryId.value && !bulkIsTransfer.value && !bulkIsPersonal.value) {
      toast.error("Pick a category, or set transfer/personal, to apply.");
      return;
    }
    try {
      const result = await bulkUpdateTransactions({
        transaction_ids: [...selectedIds.value],
        category_id: bulkCategoryId.value || undefined,
        is_transfer: bulkIsTransfer.value ? bulkIsTransfer.value === "true" : undefined,
        is_personal: bulkIsPersonal.value ? bulkIsPersonal.value === "true" : undefined,
      });
      toast.success(`${String(result.updated_count)} transaction(s) updated.`);
      if (result.closed_year_warning) toast.warning(result.closed_year_warning);
      reloadTransactions();
    } catch (err) {
      toast.error(err instanceof ApiRequestError ? err.body.message : "Bulk update failed");
    }
  }

  // --- Splits dialog ---
  const splitDialogOpen = signal(false);
  const splitTransaction = signal<BankTransaction | null>(null);
  const splitRows = signal<TransactionSplitInput[]>([]);
  const splitLoanId = signal("");
  const splitError = signal("");
  const splitSuggestionSource = signal("");
  /** Bumped only when the *set* of split rows changes — opening the dialog, adding or
   * removing a row, or a suggestion replacing them all. Editing a value inside a row
   * deliberately does not bump it: buildSplitRowEl creates fresh <input> elements, so
   * re-rendering on every keystroke destroyed the input being typed into and dropped
   * focus after each digit. Values still live in splitRows; only the DOM is pinned. */
  const splitRowsRevision = signal(0);

  function openSplitDialog(txn: BankTransaction): void {
    splitTransaction.value = txn;
    splitRows.value = txn.splits.length
      ? txn.splits.map((s) => ({
          amount: s.amount,
          category_id: s.category_id,
          loan_id: s.loan_id,
          notes: s.notes,
        }))
      : [
          { amount: "", category_id: "" },
          { amount: "", category_id: "" },
        ];
    splitLoanId.value = txn.loan_id ?? "";
    splitError.value = "";
    splitSuggestionSource.value = "";
    splitRowsRevision.value += 1;
    splitDialogOpen.value = true;
  }
  function closeSplitDialog(): void {
    splitDialogOpen.value = false;
  }
  function addSplitRow(): void {
    splitRows.value = [...splitRows.value, { amount: "", category_id: "" }];
    splitRowsRevision.value += 1;
  }
  function removeSplitRow(index: number): void {
    splitRows.value = splitRows.value.filter((_, i) => i !== index);
    splitRowsRevision.value += 1;
  }
  function setSplitRow(index: number, patch: Partial<TransactionSplitInput>): void {
    splitRows.value = splitRows.value.map((row, i) => (i === index ? { ...row, ...patch } : row));
  }

  async function suggestSplit(): Promise<void> {
    const txn = splitTransaction.value;
    if (!txn) return;
    if (!splitLoanId.value) {
      toast.error("Pick a loan first.");
      return;
    }
    const interestCategory = categories.value.find((c) => c.name === "Loan interest");
    const principalCategory = categories.value.find(
      (c) => c.name === "Loan principal repayment",
    );
    if (!interestCategory || !principalCategory) {
      toast.error("Loan interest/principal categories not found.");
      return;
    }
    try {
      const suggestion = await getSplitSuggestion(splitLoanId.value, txn.id);
      const negative = Number(txn.amount) < 0;
      const sign = negative ? "-" : "";
      splitRows.value = [
        {
          amount: `${sign}${suggestion.interest}`,
          category_id: interestCategory.id,
          loan_id: splitLoanId.value,
        },
        {
          amount: `${sign}${suggestion.principal}`,
          category_id: principalCategory.id,
          loan_id: splitLoanId.value,
        },
      ];
      splitRowsRevision.value += 1;
      splitSuggestionSource.value = describeSuggestion(suggestion);
    } catch (err) {
      toast.error(err instanceof ApiRequestError ? err.body.message : "Suggestion failed");
    }
  }

  async function saveSplits(): Promise<void> {
    const txn = splitTransaction.value;
    if (!txn) return;
    splitError.value = "";
    const rows = splitRows.value.filter((r) => r.amount.trim() && r.category_id);
    try {
      const updated = await replaceTransactionSplits(txn.id, rows);
      closeSplitDialog();
      toast.success(rows.length ? "Splits saved." : "Splits cleared.");
      if (updated.closed_year_warning) toast.warning(updated.closed_year_warning);
      reloadTransactions();
    } catch (err) {
      splitError.value = err instanceof ApiRequestError ? err.body.message : "Failed to save splits";
    }
  }

  async function unlinkInvoice(txn: BankTransaction): Promise<void> {
    if (!window.confirm("Unlink this invoice? The payment it created will be removed too.")) {
      return;
    }
    try {
      await unlinkTransactionInvoice(txn.id);
      toast.success("Unlinked.");
      reloadTransactions();
    } catch (err) {
      toast.error(err instanceof ApiRequestError ? err.body.message : "Unlink failed");
    }
  }

  // --- Manual invoice link dialog (06-banking-yearend.md: "Manual link/unlink always
  // available" — a picker for when the transaction never showed up as a suggestion). ---
  const linkInvoiceDialogOpen = signal(false);
  const linkInvoiceTransaction = signal<BankTransaction | null>(null);
  const linkInvoiceQuery = signal("");
  const linkInvoiceResults = signal<InvoiceSummary[]>([]);
  const linkInvoiceTotal = signal(0);
  const linkInvoiceSearched = signal(false);
  const linkInvoiceError = signal("");
  const allocations = signal<AllocationRow[]>([]);
  /** Same pinning as the splits dialog: rebuild allocation rows only when the set
   * changes, never per keystroke, or the amount input loses focus after each digit. */
  const allocationsRevision = signal(0);
  const allocationsSaving = signal(false);

  function openLinkInvoiceDialog(txn: BankTransaction): void {
    linkInvoiceTransaction.value = txn;
    linkInvoiceQuery.value = "";
    linkInvoiceResults.value = [];
    linkInvoiceTotal.value = 0;
    linkInvoiceSearched.value = false;
    linkInvoiceError.value = "";
    // Existing allocations load in, so add/remove/adjust later is the same screen.
    allocations.value = txn.allocations.map((a) => ({
      invoice_id: a.invoice_id,
      invoice_number: a.invoice_number,
      client_name: a.client_name,
      amount: a.amount,
      // invoice_amount_paid already counts this allocation, so back it out — otherwise
      // a saved allocation reopens reading as though it overpaid by its own amount.
      base_outstanding: sumMoney([
        subtractMoney(a.invoice_total, a.invoice_amount_paid),
        a.amount,
      ]),
    }));
    allocationsRevision.value += 1;
    linkInvoiceDialogOpen.value = true;
  }

  function allocatedTotal(): string {
    return sumMoney(allocations.value.map((a) => parseMoneyInput(a.amount) ?? "0.00"));
  }

  /** What the invoice owes ignoring this receipt entirely. `amount_paid` from the server
   * already includes whatever this transaction was previously saved against it, so that
   * part has to be added back before the dialog reasons about it. */
  function outstandingExcludingThisReceipt(invoice: InvoiceSummary): string {
    const savedHere =
      linkInvoiceTransaction.value?.allocations.find((a) => a.invoice_id === invoice.id)
        ?.amount ?? "0.00";
    return sumMoney([subtractMoney(invoice.total, invoice.amount_paid), savedHere]);
  }

  function addAllocation(invoice: InvoiceSummary): void {
    if (allocations.value.some((a) => a.invoice_id === invoice.id)) {
      toast.error("That invoice is already allocated — edit its amount instead.");
      return;
    }
    const txn = linkInvoiceTransaction.value;
    const outstanding = outstandingExcludingThisReceipt(invoice);
    const remaining = subtractMoney(absMoney(txn?.amount ?? "0.00"), absMoney(allocatedTotal()));
    // Pre-fill with whatever is smaller: what this invoice still owes, or what is left
    // of the receipt. Floored at 0 so an already-full receipt doesn't suggest a negative.
    let suggested = Number(outstanding) <= Number(remaining) ? outstanding : remaining;
    if (suggested.startsWith("-")) suggested = "0.00";
    allocations.value = [
      ...allocations.value,
      {
        invoice_id: invoice.id,
        invoice_number: invoice.number,
        client_name: invoice.client_name,
        amount: suggested,
        base_outstanding: outstanding,
      },
    ];
    allocationsRevision.value += 1;
  }

  function removeAllocation(index: number): void {
    allocations.value = allocations.value.filter((_, i) => i !== index);
    allocationsRevision.value += 1;
  }

  function setAllocationAmount(index: number, amount: string): void {
    allocations.value = allocations.value.map((row, i) =>
      i === index ? { ...row, amount } : row,
    );
  }

  async function saveAllocations(): Promise<void> {
    const txn = linkInvoiceTransaction.value;
    if (!txn) return;
    linkInvoiceError.value = "";
    allocationsSaving.value = true;
    try {
      const result = await setTransactionAllocations(
        txn.id,
        allocations.value.map((a) => ({ invoice_id: a.invoice_id, amount: a.amount })),
      );
      closeLinkInvoiceDialog();
      toast.success(
        result.transaction.allocations.length === 0
          ? "Allocations cleared."
          : `Allocated to ${String(result.transaction.allocations.length)} invoice(s).`,
      );
      if (result.over_allocation_warning) toast.warning(result.over_allocation_warning);
      reloadTransactions();
    } catch (err) {
      linkInvoiceError.value = err instanceof ApiRequestError ? err.body.message : "Save failed";
    } finally {
      allocationsSaving.value = false;
    }
  }
  function closeLinkInvoiceDialog(): void {
    linkInvoiceDialogOpen.value = false;
  }
  async function searchInvoicesForLink(): Promise<void> {
    try {
      const result = await listInvoices({
        unpaid: true,
        q: linkInvoiceQuery.value || undefined,
        page_size: LINK_INVOICE_PAGE_SIZE,
      });
      linkInvoiceResults.value = result.items;
      linkInvoiceTotal.value = result.total;
      linkInvoiceSearched.value = true;
    } catch (err) {
      linkInvoiceError.value = err instanceof ApiRequestError ? err.body.message : "Search failed";
    }
  }
  // --- Create-rule-from-transaction dialog (06-banking-yearend.md § Categorisation:
  // "the primary way rules get created"). ---
  const createRuleDialogOpen = signal(false);
  const createRuleTransaction = signal<BankTransaction | null>(null);
  const createRuleForm = form({
    pattern: "",
    category_id: "",
    set_is_transfer: false,
    set_is_personal: false,
  });
  const createRuleError = signal("");

  function openCreateRuleDialog(txn: BankTransaction): void {
    createRuleTransaction.value = txn;
    createRuleForm.reset({
      pattern: txn.description,
      category_id: txn.category_id ?? "",
      set_is_transfer: txn.is_transfer,
      set_is_personal: txn.is_personal,
    });
    createRuleError.value = "";
    createRuleDialogOpen.value = true;
  }
  function closeCreateRuleDialog(): void {
    createRuleDialogOpen.value = false;
  }
  async function saveCreateRule(): Promise<void> {
    if (!createRuleForm.fields.pattern.value.trim()) {
      createRuleError.value = "Enter a pattern to match on.";
      return;
    }
    if (
      !createRuleForm.fields.category_id.value &&
      !createRuleForm.fields.set_is_transfer.value &&
      !createRuleForm.fields.set_is_personal.value
    ) {
      createRuleError.value = "Pick a category, or flag as transfer/personal.";
      return;
    }
    try {
      await createCategoryRule({
        match_type: "contains",
        pattern: createRuleForm.fields.pattern.value,
        category_id: createRuleForm.fields.category_id.value || null,
        set_is_transfer: createRuleForm.fields.set_is_transfer.value,
        set_is_personal: createRuleForm.fields.set_is_personal.value,
      });
      closeCreateRuleDialog();
      toast.success("Rule created — it'll apply to future imports automatically.");
    } catch (err) {
      createRuleError.value = err instanceof ApiRequestError ? err.body.message : "Failed to create rule";
    }
  }

  const fragment = html`<div class="view">
    <p class="form-error"></p>

    <div class="card">
      <div class="form-row">
        <div class="form-field txn-filter-account-field">
          <label for="txn-filter-account">Account</label>
          <select id="txn-filter-account"><option value="">All</option></select>
        </div>
        <div class="form-field">
          <label for="txn-filter-from">From</label>
          <input id="txn-filter-from" type="date" />
        </div>
        <div class="form-field">
          <label for="txn-filter-to">To</label>
          <input id="txn-filter-to" type="date" />
        </div>
        <div class="form-field">
          <label for="txn-filter-q">Search</label>
          <input id="txn-filter-q" placeholder="description" />
        </div>
        <div class="checkbox-field">
          <input id="txn-filter-uncategorised" type="checkbox" />
          <label for="txn-filter-uncategorised">Uncategorised only</label>
        </div>
      </div>

      <div class="form-row bulk-actions-row">
        <div class="form-field bulk-category-field">
          <label for="bulk-category">Bulk set category</label>
          <select id="bulk-category"><option value="">Select…</option></select>
        </div>
        <div class="form-field">
          <label for="bulk-transfer">Transfer</label>
          <select id="bulk-transfer">
            <option value="">(no change)</option>
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>
        </div>
        <div class="form-field">
          <label for="bulk-personal">Personal</label>
          <select id="bulk-personal">
            <option value="">(no change)</option>
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>
        </div>
        <div class="form-actions">
          <button class="btn bulk-apply-btn" type="button">Apply to selected</button>
        </div>
      </div>

      <div class="data-table transactions-table">
        <table>
          <thead>
            <tr>
              <th><input type="checkbox" class="txn-select-all" /></th>
              <th>Date</th>
              <th>Account</th>
              <th>Description</th>
              <th>Amount</th>
              <th>Category</th>
              <th>Loan</th>
              <th>Invoice</th>
              <th>Flags</th>
              <th>Notes</th>
              <th></th>
            </tr>
          </thead>
          <tbody class="transactions-body"></tbody>
        </table>
      </div>

      <dialog class="link-invoice-dialog">
        <div class="card-label">Link to invoice</div>
        <p class="link-invoice-subject"></p>
        <p class="allocation-summary"></p>
        <p class="form-error link-invoice-error"></p>
        <div class="allocation-panel">
          <div class="card-label">Allocated</div>
          <div class="allocation-rows"></div>
        </div>
        <div class="form-field">
          <label for="link-invoice-search">Search unpaid invoices</label>
          <input id="link-invoice-search" placeholder="number or client name" />
        </div>
        <div class="data-table link-invoice-results">
          <table>
            <thead>
              <tr>
                <th>Number</th>
                <th>Issued</th>
                <th>Client</th>
                <th>Outstanding</th>
                <th></th>
              </tr>
            </thead>
            <tbody class="link-invoice-results-body"></tbody>
          </table>
        </div>
        <p class="field-hint link-invoice-count"></p>
        <div class="form-actions">
          <button class="btn btn-primary save-allocations-btn" type="button">
            Save allocations
          </button>
          <button class="btn cancel-link-invoice-btn" type="button">Cancel</button>
        </div>
      </dialog>

      <dialog class="create-rule-dialog">
        <div class="card-label">Create a rule from this transaction</div>
        <p class="form-error create-rule-error"></p>
        <div class="form-field">
          <label for="create-rule-pattern">Match description containing</label>
          <input id="create-rule-pattern" />
          <p class="field-hint">
            Pre-filled with the transaction's full description — trim it down to the
            meaningful part (e.g. the merchant name) so future transactions match too.
          </p>
        </div>
        <div class="form-field">
          <label for="create-rule-category">Category</label>
          <select id="create-rule-category"><option value="">(none)</option></select>
        </div>
        <div class="form-row">
          <div class="checkbox-field">
            <input id="create-rule-transfer" type="checkbox" />
            <label for="create-rule-transfer">Flag transfer</label>
          </div>
          <div class="checkbox-field">
            <input id="create-rule-personal" type="checkbox" />
            <label for="create-rule-personal">Flag personal</label>
          </div>
        </div>
        <div class="form-actions">
          <button class="btn btn-primary save-create-rule-btn" type="button">Create rule</button>
          <button class="btn cancel-create-rule-btn" type="button">Cancel</button>
        </div>
      </dialog>

      <div class="pager">
        <button class="btn pager-prev" type="button">Previous</button>
        <span class="pager-label"></span>
        <button class="btn pager-next" type="button">Next</button>
      </div>
    </div>

    <dialog class="split-transaction-dialog">
      <div class="card-label">Split transaction</div>
      <p class="form-error split-error"></p>
      <div class="form-row">
        <div class="form-field">
          <label for="split-loan">Loan (for suggestion)</label>
          <select id="split-loan"><option value="">(none)</option></select>
        </div>
        <div class="form-actions">
          <button class="btn suggest-split-btn" type="button">Suggest split</button>
        </div>
      </div>
      <p class="field-hint split-suggestion-source"></p>
      <div class="split-rows"></div>
      <p class="field-hint split-total-hint"></p>
      <div class="form-actions">
        <button class="btn add-split-row-btn" type="button">Add row</button>
      </div>
      <div class="form-actions">
        <button class="btn btn-primary save-splits-btn" type="button">Save splits</button>
        <button class="btn cancel-splits-btn" type="button">Cancel</button>
      </div>
    </dialog>
  </div>`;

  const viewEl = fragment.querySelector<HTMLElement>(".view");
  if (!viewEl) throw new Error("banking view produced no .view element");
  viewEl.insertBefore(buildBankingSubNav("transactions"), viewEl.firstChild);

  const errorEl = fragment.querySelector<HTMLParagraphElement>(".form-error");

  const filterAccountSelect = fragment.querySelector<HTMLSelectElement>("#txn-filter-account");
  const filterFromInput = fragment.querySelector<HTMLInputElement>("#txn-filter-from");
  const filterToInput = fragment.querySelector<HTMLInputElement>("#txn-filter-to");
  const filterQInput = fragment.querySelector<HTMLInputElement>("#txn-filter-q");
  const filterUncategorisedInput =
    fragment.querySelector<HTMLInputElement>("#txn-filter-uncategorised");
  const bulkCategorySelect = fragment.querySelector<HTMLSelectElement>("#bulk-category");
  const bulkTransferSelect = fragment.querySelector<HTMLSelectElement>("#bulk-transfer");
  const bulkPersonalSelect = fragment.querySelector<HTMLSelectElement>("#bulk-personal");
  const bulkApplyBtn = fragment.querySelector<HTMLButtonElement>(".bulk-apply-btn");
  const selectAllCheckbox = fragment.querySelector<HTMLInputElement>(".txn-select-all");
  const transactionsTable = fragment.querySelector<HTMLTableElement>(".transactions-table table");
  const transactionsBody = fragment.querySelector<HTMLTableSectionElement>(".transactions-body");
  const pagerEl = fragment.querySelector<HTMLElement>(".pager");
  const pagerPrevBtn = fragment.querySelector<HTMLButtonElement>(".pager-prev");
  const pagerNextBtn = fragment.querySelector<HTMLButtonElement>(".pager-next");
  const pagerLabelEl = fragment.querySelector<HTMLElement>(".pager-label");

  const splitDialogEl = fragment.querySelector<HTMLDialogElement>(".split-transaction-dialog");
  const splitErrorEl = fragment.querySelector<HTMLParagraphElement>(".split-error");
  const splitLoanSelect = fragment.querySelector<HTMLSelectElement>("#split-loan");
  const splitSuggestionSourceEl = fragment.querySelector<HTMLElement>(".split-suggestion-source");
  const suggestSplitBtn = fragment.querySelector<HTMLButtonElement>(".suggest-split-btn");
  const splitRowsEl = fragment.querySelector<HTMLElement>(".split-rows");
  const splitTotalHintEl = fragment.querySelector<HTMLElement>(".split-total-hint");
  const addSplitRowBtn = fragment.querySelector<HTMLButtonElement>(".add-split-row-btn");
  const saveSplitsBtn = fragment.querySelector<HTMLButtonElement>(".save-splits-btn");
  const cancelSplitsBtn = fragment.querySelector<HTMLButtonElement>(".cancel-splits-btn");

  const linkInvoiceDialogEl = fragment.querySelector<HTMLDialogElement>(".link-invoice-dialog");
  const linkInvoiceErrorEl = fragment.querySelector<HTMLParagraphElement>(".link-invoice-error");
  const linkInvoiceSearchInput = fragment.querySelector<HTMLInputElement>("#link-invoice-search");
  const linkInvoiceResultsTable = fragment.querySelector<HTMLTableElement>(
    ".link-invoice-results table",
  );
  const linkInvoiceResultsBody = fragment.querySelector<HTMLTableSectionElement>(
    ".link-invoice-results-body",
  );
  const cancelLinkInvoiceBtn = fragment.querySelector<HTMLButtonElement>(
    ".cancel-link-invoice-btn",
  );
  const linkInvoiceSubjectEl = fragment.querySelector<HTMLElement>(".link-invoice-subject");
  const linkInvoiceCountEl = fragment.querySelector<HTMLElement>(".link-invoice-count");
  const allocationSummaryEl = fragment.querySelector<HTMLElement>(".allocation-summary");
  const allocationPanelEl = fragment.querySelector<HTMLElement>(".allocation-panel");
  const allocationRowsEl = fragment.querySelector<HTMLElement>(".allocation-rows");
  const saveAllocationsBtn = fragment.querySelector<HTMLButtonElement>(".save-allocations-btn");

  const createRuleDialogEl = fragment.querySelector<HTMLDialogElement>(".create-rule-dialog");
  const createRuleErrorEl = fragment.querySelector<HTMLParagraphElement>(".create-rule-error");
  const createRulePatternInput = fragment.querySelector<HTMLInputElement>("#create-rule-pattern");
  const createRuleCategorySelect =
    fragment.querySelector<HTMLSelectElement>("#create-rule-category");
  const createRuleTransferInput =
    fragment.querySelector<HTMLInputElement>("#create-rule-transfer");
  const createRulePersonalInput =
    fragment.querySelector<HTMLInputElement>("#create-rule-personal");
  const saveCreateRuleBtn = fragment.querySelector<HTMLButtonElement>(".save-create-rule-btn");
  const cancelCreateRuleBtn = fragment.querySelector<HTMLButtonElement>(".cancel-create-rule-btn");

  if (
    !errorEl ||
    !filterAccountSelect ||
    !filterFromInput ||
    !filterToInput ||
    !filterQInput ||
    !filterUncategorisedInput ||
    !bulkCategorySelect ||
    !bulkTransferSelect ||
    !bulkPersonalSelect ||
    !bulkApplyBtn ||
    !selectAllCheckbox ||
    !transactionsTable ||
    !transactionsBody ||
    !pagerEl ||
    !pagerPrevBtn ||
    !pagerNextBtn ||
    !pagerLabelEl ||
    !splitDialogEl ||
    !splitErrorEl ||
    !splitLoanSelect ||
    !splitSuggestionSourceEl ||
    !suggestSplitBtn ||
    !splitRowsEl ||
    !splitTotalHintEl ||
    !addSplitRowBtn ||
    !saveSplitsBtn ||
    !cancelSplitsBtn ||
    !linkInvoiceDialogEl ||
    !linkInvoiceErrorEl ||
    !linkInvoiceSearchInput ||
    !linkInvoiceResultsTable ||
    !linkInvoiceResultsBody ||
    !linkInvoiceSubjectEl ||
    !linkInvoiceCountEl ||
    !allocationSummaryEl ||
    !allocationPanelEl ||
    !allocationRowsEl ||
    !saveAllocationsBtn ||
    !cancelLinkInvoiceBtn ||
    !createRuleDialogEl ||
    !createRuleErrorEl ||
    !createRulePatternInput ||
    !createRuleCategorySelect ||
    !createRuleTransferInput ||
    !createRulePersonalInput ||
    !saveCreateRuleBtn ||
    !cancelCreateRuleBtn
  ) {
    throw new Error("banking view markup missing expected elements");
  }

  bindErrorText(errorEl, loadError);

  currentScope().effect(() => {
    setSelectOptions(
      filterAccountSelect,
      accounts.value.map((a) => ({ value: a.id, label: a.name })),
      { placeholder: "All" },
    );
  });
  filterAccountSelect.addEventListener("change", () => {
    accountFilter.value = filterAccountSelect.value;
    filtersChanged();
  });
  filterFromInput.addEventListener("change", () => {
    dateFromFilter.value = filterFromInput.value;
    filtersChanged();
  });
  filterToInput.addEventListener("change", () => {
    dateToFilter.value = filterToInput.value;
    filtersChanged();
  });
  filterQInput.addEventListener("change", () => {
    searchQuery.value = filterQInput.value;
    filtersChanged();
  });
  filterUncategorisedInput.addEventListener("change", () => {
    uncategorisedOnly.value = filterUncategorisedInput.checked;
    filtersChanged();
  });

  currentScope().effect(() => {
    setSelectOptions(
      bulkCategorySelect,
      categories.value.map((c) => ({ value: c.id, label: c.name })),
      { placeholder: "Select…" },
    );
  });
  bulkCategorySelect.addEventListener("change", () => {
    bulkCategoryId.value = bulkCategorySelect.value;
  });
  bulkTransferSelect.addEventListener("change", () => {
    bulkIsTransfer.value = bulkTransferSelect.value as "" | "true" | "false";
  });
  bulkPersonalSelect.addEventListener("change", () => {
    bulkIsPersonal.value = bulkPersonalSelect.value as "" | "true" | "false";
  });
  bulkApplyBtn.addEventListener("click", () => {
    void applyBulk();
  });

  // "Select all" only ever acts on the transactions currently loaded (the current
  // filtered/searched page) — never the whole unfiltered table — since `transactions`
  // already holds exactly that set.
  selectAllCheckbox.addEventListener("change", () => {
    selectedIds.value = selectAllCheckbox.checked
      ? new Set(transactions.value.map((t) => t.id))
      : new Set();
  });
  currentScope().effect(() => {
    const visibleIds = transactions.value.map((t) => t.id);
    const selectedCount = visibleIds.filter((id) => selectedIds.value.has(id)).length;
    selectAllCheckbox.checked = visibleIds.length > 0 && selectedCount === visibleIds.length;
    selectAllCheckbox.indeterminate = selectedCount > 0 && selectedCount < visibleIds.length;
  });

  const pageCount = computed(() => Math.max(1, Math.ceil(total.value / TRANSACTIONS_PAGE_SIZE)));
  currentScope().effect(() => {
    pagerEl.style.display = pageCount.value > 1 ? "" : "none";
    pagerLabelEl.textContent = `Page ${String(page.value)} of ${String(pageCount.value)}`;
    pagerPrevBtn.disabled = page.value <= 1;
    pagerNextBtn.disabled = page.value >= pageCount.value;
  });
  pagerPrevBtn.addEventListener("click", () => {
    if (page.value > 1) {
      page.value -= 1;
      reloadTransactions();
    }
  });
  pagerNextBtn.addEventListener("click", () => {
    if (page.value < pageCount.value) {
      page.value += 1;
      reloadTransactions();
    }
  });

  currentScope().effect(() => {
    setSelectOptions(
      splitLoanSelect,
      loans.value.map((l) => ({ value: l.id, label: loanOptionLabel(l) })),
      { placeholder: "(none)" },
    );
    splitLoanSelect.value = splitLoanId.value;
  });
  splitLoanSelect.addEventListener("change", () => {
    splitLoanId.value = splitLoanSelect.value;
    splitSuggestionSource.value = "";
  });
  currentScope().effect(() => {
    splitSuggestionSourceEl.textContent = splitSuggestionSource.value;
  });
  bindErrorText(splitErrorEl, splitError);
  suggestSplitBtn.addEventListener("click", () => {
    void suggestSplit();
  });
  addSplitRowBtn.addEventListener("click", addSplitRow);
  saveSplitsBtn.addEventListener("click", () => {
    void saveSplits();
  });
  cancelSplitsBtn.addEventListener("click", closeSplitDialog);
  splitDialogEl.addEventListener("close", () => {
    splitDialogOpen.value = false;
  });
  currentScope().effect(() => {
    if (splitDialogOpen.value && !splitDialogEl.open) splitDialogEl.showModal();
    if (!splitDialogOpen.value && splitDialogEl.open) splitDialogEl.close();
  });

  bindErrorText(linkInvoiceErrorEl, linkInvoiceError);
  linkInvoiceSearchInput.addEventListener("input", () => {
    linkInvoiceQuery.value = linkInvoiceSearchInput.value;
    void searchInvoicesForLink();
  });
  cancelLinkInvoiceBtn.addEventListener("click", closeLinkInvoiceDialog);
  linkInvoiceDialogEl.addEventListener("close", () => {
    linkInvoiceDialogOpen.value = false;
  });
  currentScope().effect(() => {
    if (linkInvoiceDialogOpen.value && !linkInvoiceDialogEl.open) {
      linkInvoiceDialogEl.showModal();
      void searchInvoicesForLink();
    }
    if (!linkInvoiceDialogOpen.value && linkInvoiceDialogEl.open) linkInvoiceDialogEl.close();
  });
  // Which bank row this dialog is about. The invoice list can run to dozens of
  // near-identical monthly invoices from one client, and picking the wrong one silently
  // creates a payment against it — so the amount and date being reconciled stay on screen
  // rather than only in the row behind the modal.
  currentScope().effect(() => {
    const txn = linkInvoiceTransaction.value;
    linkInvoiceSubjectEl.textContent = txn
      ? `Reconciling ${formatDate(txn.date)} · ${formatMoney(txn.amount)} · ${txn.description}`
      : "";
  });
  bindShow(
    allocationPanelEl,
    computed(() => allocations.value.length > 0),
  );
  currentScope().effect(() => {
    void allocationsRevision.value;
    allocationRowsEl.replaceChildren();
    // peek(), so editing an amount updates the signal without rebuilding the input the
    // user is typing into.
    allocations.peek().forEach((row, index) => {
      allocationRowsEl.appendChild(
        buildAllocationRowEl(row, index, setAllocationAmount, removeAllocation),
      );
    });
  });
  currentScope().effect(() => {
    const txn = linkInvoiceTransaction.value;
    if (!txn) {
      allocationSummaryEl.textContent = "";
      allocationSummaryEl.classList.remove("allocation-over");
      return;
    }
    const allocated = allocatedTotal();
    const receipt = absMoney(txn.amount);
    const remaining = subtractMoney(receipt, absMoney(allocated));
    const over = remaining.startsWith("-");
    allocationSummaryEl.classList.toggle("allocation-over", over);
    // Deliberately about the receipt, not the invoices — "£0.00 left" means the money is
    // all spoken for, which is not the same as every invoice being settled. The per-row
    // outcome lines answer that second question.
    allocationSummaryEl.textContent = over
      ? `Allocated ${formatMoney(allocated)} of ${formatMoney(txn.amount)} — ` +
        `${formatMoney(absMoney(remaining))} more than the receipt. ` +
        "The invoices will show as overpaid."
      : `Allocated ${formatMoney(allocated)} of this ${formatMoney(txn.amount)} receipt · ` +
        `${formatMoney(remaining)} of it left to allocate`;
  });
  saveAllocationsBtn.addEventListener("click", () => {
    void saveAllocations();
  });
  currentScope().effect(() => {
    saveAllocationsBtn.disabled = allocationsSaving.value;
  });

  // The dialog has no pager, so a capped result set has to announce itself — otherwise
  // the list simply stops and nothing says there is more behind it.
  currentScope().effect(() => {
    if (!linkInvoiceSearched.value) {
      linkInvoiceCountEl.textContent = "";
      return;
    }
    const shown = linkInvoiceResults.value.length;
    const total = linkInvoiceTotal.value;
    if (total === 0) {
      linkInvoiceCountEl.textContent = linkInvoiceQuery.value
        ? "No unpaid invoices match that search."
        : "No unpaid invoices. Only issued, sent, part-paid and overdue invoices can be linked.";
    } else if (shown < total) {
      linkInvoiceCountEl.textContent =
        `Showing ${String(shown)} of ${String(total)} unpaid invoices — ` +
        "search by number or client name to narrow it down.";
    } else {
      linkInvoiceCountEl.textContent = `Showing all ${String(total)} unpaid invoice(s).`;
    }
  });
  list(
    linkInvoiceResultsBody,
    linkInvoiceResults,
    (inv) => inv.id,
    (inv) => {
      const row = html`<tr>
        <td>${inv.number ?? "(draft)"}</td>
        <td>${formatDate(inv.issue_date)}</td>
        <td>${inv.client_name}</td>
        <td class="link-invoice-outstanding"></td>
        <td><button class="btn link-invoice-pick-btn" type="button">Add</button></td>
      </tr>`.firstElementChild;
      if (!row) throw new Error("link-invoice result row template produced no element");
      const pickBtn = row.querySelector<HTMLButtonElement>(".link-invoice-pick-btn");
      const outstandingCell = row.querySelector<HTMLElement>(".link-invoice-outstanding");
      // Nets off whatever is pending in the panel above, so the same invoice can't read
      // as owing its full amount while sitting there allocated. list() gives every row
      // its own disposed scope, so this effect is safe here.
      currentScope().effect(() => {
        const pending = allocations.value.find((a) => a.invoice_id === inv.id);
        const outstanding = subtractMoney(
          outstandingExcludingThisReceipt(inv),
          pending ? (parseMoneyInput(pending.amount) ?? "0.00") : "0.00",
        );
        if (outstandingCell) outstandingCell.textContent = formatMoney(outstanding, inv.currency);
        if (pickBtn) {
          pickBtn.disabled = pending !== undefined;
          pickBtn.textContent = pending ? "Allocated" : "Add";
        }
      });
      pickBtn?.addEventListener("click", () => {
        addAllocation(inv);
      });
      return labelRow(row, linkInvoiceResultsTable);
    },
  );

  currentScope().effect(() => {
    setSelectOptions(
      createRuleCategorySelect,
      categories.value.map((c) => ({ value: c.id, label: c.name })),
      { placeholder: "(none)" },
    );
  });
  bindValue(createRulePatternInput, createRuleForm.fields.pattern);
  bindSelect(createRuleCategorySelect, createRuleForm.fields.category_id);
  bindCheckbox(createRuleTransferInput, createRuleForm.fields.set_is_transfer);
  bindCheckbox(createRulePersonalInput, createRuleForm.fields.set_is_personal);
  bindErrorText(createRuleErrorEl, createRuleError);
  saveCreateRuleBtn.addEventListener("click", () => {
    void saveCreateRule();
  });
  cancelCreateRuleBtn.addEventListener("click", closeCreateRuleDialog);
  createRuleDialogEl.addEventListener("close", () => {
    createRuleDialogOpen.value = false;
  });
  currentScope().effect(() => {
    if (createRuleDialogOpen.value && !createRuleDialogEl.open) createRuleDialogEl.showModal();
    if (!createRuleDialogOpen.value && createRuleDialogEl.open) createRuleDialogEl.close();
  });

  currentScope().effect(() => {
    const txn = splitTransaction.value;
    // Structure only — see splitRowsRevision. peek() reads the current values without
    // subscribing to them, so a keystroke updates the signal but not the DOM.
    void splitRowsRevision.value;
    splitRowsEl.replaceChildren();
    if (!txn) return;
    splitRows.peek().forEach((row, index) => {
      splitRowsEl.appendChild(
        buildSplitRowEl(row, index, categories.value, loans.value, setSplitRow, removeSplitRow),
      );
    });
  });

  // The running total does follow every keystroke — it is text, so rewriting it can't
  // disturb focus.
  currentScope().effect(() => {
    const txn = splitTransaction.value;
    if (!txn) {
      splitTotalHintEl.textContent = "";
      return;
    }
    const sum = splitRows.value.reduce((acc, r) => acc + (Number(r.amount) || 0), 0);
    const parent = Number(txn.amount);
    splitTotalHintEl.textContent =
      `Splits total ${formatMoney(sum.toFixed(2))} — transaction is ${formatMoney(txn.amount)}` +
      (Math.abs(sum - parent) > 0.001 ? " (must match exactly to save)" : "");
  });

  list(
    transactionsBody,
    transactions,
    (t) => t.id,
    (t) =>
      buildTransactionRow(t, transactionsTable, categories, loans, {
        onChanged: reloadTransactions,
        onSplit: openSplitDialog,
        onToggleSelected: toggleSelected,
        isSelected: () => selectedIds.value.has(t.id),
        onLinkInvoice: openLinkInvoiceDialog,
        onUnlinkInvoice: (t2) => {
          void unlinkInvoice(t2);
        },
        onCreateRule: openCreateRuleDialog,
      }),
  );

  return fragment;
}

function buildAllocationRowEl(
  row: AllocationRow,
  index: number,
  onAmountChange: (index: number, amount: string) => void,
  onRemove: (index: number) => void,
): Node {
  const wrapper = document.createElement("div");
  wrapper.className = "form-row allocation-row";

  const label = document.createElement("div");
  label.className = "form-field allocation-label";
  label.textContent = `${row.invoice_number ?? "(draft)"} — ${row.client_name}`;

  const amountField = document.createElement("div");
  amountField.className = "form-field";
  const amountInput = document.createElement("input");
  amountInput.inputMode = "decimal";
  amountInput.className = "allocation-amount";
  amountInput.value = row.amount;
  const outcome = document.createElement("small");
  outcome.className = "field-hint allocation-outcome";
  outcome.textContent = describeAllocationOutcome(row);
  amountInput.addEventListener("input", () => {
    onAmountChange(index, amountInput.value);
    // Updated in place rather than through a signal effect: rebuilding this row on every
    // keystroke is exactly what cost the input its focus before.
    outcome.textContent = describeAllocationOutcome({ ...row, amount: amountInput.value });
  });
  amountField.appendChild(amountInput);
  amountField.appendChild(outcome);

  const actions = document.createElement("div");
  actions.className = "form-actions";
  const removeBtn = document.createElement("button");
  removeBtn.type = "button";
  removeBtn.className = "btn btn-danger allocation-remove-btn";
  removeBtn.textContent = "Remove";
  removeBtn.addEventListener("click", () => {
    onRemove(index);
  });
  actions.appendChild(removeBtn);

  wrapper.appendChild(label);
  wrapper.appendChild(amountField);
  wrapper.appendChild(actions);
  return wrapper;
}

function buildSplitRowEl(
  row: TransactionSplitInput,
  index: number,
  categories: Category[],
  loans: LoanSummary[],
  onChange: (index: number, patch: Partial<TransactionSplitInput>) => void,
  onRemove: (index: number) => void,
): Node {
  const wrapper = document.createElement("div");
  wrapper.className = "form-row split-row";

  const amountField = document.createElement("div");
  amountField.className = "form-field";
  const amountInput = document.createElement("input");
  amountInput.inputMode = "decimal";
  amountInput.value = row.amount;
  amountInput.placeholder = "Amount";
  amountInput.addEventListener("input", () => {
    onChange(index, { amount: amountInput.value });
  });
  amountField.appendChild(amountInput);

  const categoryField = document.createElement("div");
  categoryField.className = "form-field";
  const categorySelect = document.createElement("select");
  const placeholderOpt = document.createElement("option");
  placeholderOpt.value = "";
  placeholderOpt.textContent = "Category…";
  categorySelect.appendChild(placeholderOpt);
  for (const c of categories) {
    const opt = document.createElement("option");
    opt.value = c.id;
    opt.textContent = c.name;
    categorySelect.appendChild(opt);
  }
  categorySelect.value = row.category_id;
  categorySelect.addEventListener("change", () => {
    onChange(index, { category_id: categorySelect.value });
  });
  categoryField.appendChild(categorySelect);

  const loanField = document.createElement("div");
  loanField.className = "form-field";
  const loanSelect = document.createElement("select");
  const noLoanOpt = document.createElement("option");
  noLoanOpt.value = "";
  noLoanOpt.textContent = "(no loan)";
  loanSelect.appendChild(noLoanOpt);
  for (const l of loans) {
    const opt = document.createElement("option");
    opt.value = l.id;
    opt.textContent = loanOptionLabel(l);
    loanSelect.appendChild(opt);
  }
  loanSelect.value = row.loan_id ?? "";
  loanSelect.addEventListener("change", () => {
    onChange(index, { loan_id: loanSelect.value || null });
  });
  loanField.appendChild(loanSelect);

  const notesField = document.createElement("div");
  notesField.className = "form-field";
  const notesInput = document.createElement("input");
  notesInput.value = row.notes ?? "";
  notesInput.placeholder = "Notes";
  notesInput.addEventListener("input", () => {
    onChange(index, { notes: notesInput.value || null });
  });
  notesField.appendChild(notesInput);

  const removeBtn = document.createElement("button");
  removeBtn.type = "button";
  removeBtn.className = "btn btn-danger";
  removeBtn.textContent = "Remove";
  removeBtn.addEventListener("click", () => {
    onRemove(index);
  });

  wrapper.appendChild(amountField);
  wrapper.appendChild(categoryField);
  wrapper.appendChild(loanField);
  wrapper.appendChild(notesField);
  wrapper.appendChild(removeBtn);
  return wrapper;
}

interface TransactionRowCallbacks {
  onChanged: () => void;
  onSplit: (txn: BankTransaction) => void;
  onToggleSelected: (id: string, checked: boolean) => void;
  isSelected: () => boolean;
  onLinkInvoice: (txn: BankTransaction) => void;
  onUnlinkInvoice: (txn: BankTransaction) => void;
  onCreateRule: (txn: BankTransaction) => void;
}

function buildTransactionRow(
  txn: BankTransaction,
  table: HTMLTableElement,
  categoriesSignal: { value: Category[] },
  loansSignal: { value: LoanSummary[] },
  callbacks: TransactionRowCallbacks,
): Element {
  const categoryId = signal(txn.category_id ?? "");
  const isTransfer = signal(txn.is_transfer);
  const isPersonal = signal(txn.is_personal);

  async function saveField(patch: {
    category_id?: string | null;
    is_transfer?: boolean;
    is_personal?: boolean;
    loan_id?: string | null;
    notes?: string | null;
  }): Promise<void> {
    try {
      const updated = await updateTransaction(txn.id, {
        category_id: patch.category_id !== undefined ? patch.category_id : categoryId.value || null,
        is_transfer: patch.is_transfer ?? isTransfer.value,
        is_personal: patch.is_personal ?? isPersonal.value,
        notes: patch.notes !== undefined ? patch.notes : txn.notes,
        loan_id: patch.loan_id !== undefined ? patch.loan_id : txn.loan_id,
        updated_at: txn.updated_at,
      });
      if (updated.closed_year_warning) toast.warning(updated.closed_year_warning);
      callbacks.onChanged();
    } catch (err) {
      toast.error(err instanceof ApiRequestError ? err.body.message : "Update failed");
    }
  }

  const row = html`<tr>
    <td><input type="checkbox" class="txn-select" /></td>
    <td>${txn.date}</td>
    <td>${txn.bank_account_name}</td>
    <td>${txn.description}</td>
    <td>${formatMoney(txn.amount)}</td>
    <td>
      <span class="txn-splits-badge">Split</span>
      <select class="txn-category-select"></select>
    </td>
    <td><select class="txn-loan-select"></select></td>
    <td>
      <span class="txn-invoice-number"></span>
      <button class="btn txn-link-invoice-btn" type="button">Link</button>
      <button class="btn btn-danger txn-unlink-invoice-btn" type="button">Unlink</button>
    </td>
    <td>
      <label class="checkbox-field"><input type="checkbox" class="txn-transfer" /> Transfer</label>
      <label class="checkbox-field"><input type="checkbox" class="txn-personal" /> Personal</label>
    </td>
    <td><input class="txn-notes-input" placeholder="Notes" /></td>
    <td>
      <button class="btn txn-split-btn" type="button">Split</button>
      <button class="btn txn-create-rule-btn" type="button">Create rule</button>
    </td>
  </tr>`.firstElementChild;
  if (!row) throw new Error("transaction row template produced no element");

  const selectCheckbox = row.querySelector<HTMLInputElement>(".txn-select");
  const splitsBadge = row.querySelector<HTMLElement>(".txn-splits-badge");
  const categorySelect = row.querySelector<HTMLSelectElement>(".txn-category-select");
  const loanSelect = row.querySelector<HTMLSelectElement>(".txn-loan-select");
  const invoiceNumberEl = row.querySelector<HTMLElement>(".txn-invoice-number");
  const linkInvoiceBtn = row.querySelector<HTMLButtonElement>(".txn-link-invoice-btn");
  const unlinkInvoiceBtn = row.querySelector<HTMLButtonElement>(".txn-unlink-invoice-btn");
  const transferInput = row.querySelector<HTMLInputElement>(".txn-transfer");
  const personalInput = row.querySelector<HTMLInputElement>(".txn-personal");
  const notesInput = row.querySelector<HTMLInputElement>(".txn-notes-input");
  const splitBtn = row.querySelector<HTMLButtonElement>(".txn-split-btn");
  const createRuleBtn = row.querySelector<HTMLButtonElement>(".txn-create-rule-btn");
  if (
    !selectCheckbox ||
    !splitsBadge ||
    !categorySelect ||
    !loanSelect ||
    !invoiceNumberEl ||
    !linkInvoiceBtn ||
    !unlinkInvoiceBtn ||
    !transferInput ||
    !personalInput ||
    !notesInput ||
    !splitBtn ||
    !createRuleBtn
  ) {
    throw new Error("transaction row markup missing expected elements");
  }

  // Reactive (not a one-time assignment): "select all" toggles selectedIds from
  // outside this row entirely, so the checkbox's own visual state must follow the
  // signal, not just the row's own click handler.
  currentScope().effect(() => {
    selectCheckbox.checked = callbacks.isSelected();
  });
  selectCheckbox.addEventListener("change", () => {
    callbacks.onToggleSelected(txn.id, selectCheckbox.checked);
  });

  setSelectOptions(
    categorySelect,
    categoriesSignal.value.map((c) => ({ value: c.id, label: c.name })),
    { placeholder: "Uncategorised" },
  );
  categorySelect.value = txn.category_id ?? "";
  bindShow(categorySelect, computed(() => !txn.has_splits));
  bindShow(splitsBadge, computed(() => txn.has_splits));
  categorySelect.addEventListener("change", () => {
    categoryId.value = categorySelect.value;
    void saveField({ category_id: categorySelect.value || null });
  });

  // Direct loan link on the whole transaction — independent of, and disabled
  // alongside, per-split loan links (a split transaction's own loan_id is never read
  // by loan history, so leaving the control live for it would be a dead no-op).
  setSelectOptions(
    loanSelect,
    loansSignal.value.map((l) => ({ value: l.id, label: loanOptionLabel(l) })),
    { placeholder: "(none)" },
  );
  loanSelect.value = txn.loan_id ?? "";
  bindShow(loanSelect, computed(() => !txn.has_splits));
  loanSelect.addEventListener("change", () => {
    void saveField({ loan_id: loanSelect.value || null });
  });

  // Every invoice this receipt settled — one bank row can cover several (Phase 8b).
  invoiceNumberEl.textContent = txn.allocations
    .map((a) => a.invoice_number ?? "(draft)")
    .join(", ");
  const isAllocated = computed(() => txn.allocations.length > 0);
  bindShow(invoiceNumberEl, isAllocated);
  // Stays available once allocated, as "Edit" — set_allocations diffs rather than
  // replaces precisely so an allocation can be adjusted without disturbing the others,
  // and hiding this left Unlink-everything-and-redo as the only way to change one amount.
  linkInvoiceBtn.textContent = isAllocated.value ? "Edit" : "Link";
  bindShow(unlinkInvoiceBtn, isAllocated);
  linkInvoiceBtn.addEventListener("click", () => {
    callbacks.onLinkInvoice(txn);
  });
  unlinkInvoiceBtn.addEventListener("click", () => {
    callbacks.onUnlinkInvoice(txn);
  });

  bindCheckbox(transferInput, isTransfer);
  transferInput.addEventListener("change", () => {
    void saveField({ is_transfer: transferInput.checked });
  });
  bindCheckbox(personalInput, isPersonal);
  personalInput.addEventListener("change", () => {
    void saveField({ is_personal: personalInput.checked });
  });

  notesInput.value = txn.notes ?? "";
  notesInput.addEventListener("change", () => {
    void saveField({ notes: notesInput.value || null });
  });

  splitBtn.addEventListener("click", () => {
    callbacks.onSplit(txn);
  });
  createRuleBtn.addEventListener("click", () => {
    callbacks.onCreateRule(txn);
  });

  return labelRow(row, table);
}
